import { useState, useEffect, useCallback } from "react";
import WindowSearch from "@/components/WindowSearch";
import { api } from "@/lib/api";
import { fmtBytes } from "@/lib/format";
import { RefreshCw, Search, X, AlertTriangle, HardDrive, FolderOpen, Loader2 } from "lucide-react";

const FILTERS = [
  ["", "All users"], ["0", "0 projects"], ["1-2", "1–2 projects"], ["3-5", "3–5 projects"],
  ["6-10", "6–10 projects"], ["11+", "11+ projects"], ["recent", "Active (30d)"], ["inactive", "Inactive"],
];
const SORTS = [
  ["storage", "Storage used"], ["projects", "Project count"], ["exports", "Exports"],
  ["media", "Media files"], ["last_activity", "Last activity"], ["created", "Created date"],
];

const fmtDate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString(); } catch { return "—"; } };

function ProjectsBadge({ n }) {
  return <span data-testid="usage-projects-badge" className="rounded-full border border-white/15 bg-white/[0.02] px-2 py-0.5 text-xs font-bold text-white">{n}</span>;
}

function DetailDrawer({ uid, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setD(null); setErr(null);
    api.adminUserUsageDetail(uid).then(setD).catch((e) => setErr(e?.response?.data?.detail || "Failed to load user usage"));
  }, [uid]);
  const BREAK = [["originals", "Originals"], ["optimized", "Optimized"], ["proxy", "Proxies"],
    ["exports", "Exports"], ["user_images", "User Images"], ["user_audio", "User Audio"], ["ai_cutouts", "AI Cut-outs"]];
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={onClose} data-testid="usage-detail-drawer">
      <div className="relative h-full w-full max-w-lg overflow-y-auto border-l border-white/10 bg-[#0b0b0b] p-6" onClick={(e) => e.stopPropagation()}>
        <WindowSearch />
        <div className="mb-4 flex items-start justify-between">
          <h3 className="text-lg font-bold text-white">User usage</h3>
          <button onClick={onClose} data-testid="usage-detail-close" className="rounded-full p-1 text-[#A1A1AA] hover:text-white"><X size={18} /></button>
        </div>
        {err ? <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{err}</div>
          : !d ? <div className="flex items-center gap-2 text-sm text-[#A1A1AA]"><Loader2 size={16} className="animate-spin" /> Loading…</div>
          : (
          <div className="space-y-5">
            <div>
              <div className="text-sm font-bold text-white">{d.user.name || d.user.email}</div>
              <div className="text-xs text-[#A1A1AA]">{d.user.email} · {d.user.plan} · {d.user.status}</div>
              <div className="mt-1 text-xs text-[#666]">Last active {fmtDate(d.user.last_active)} · Joined {fmtDate(d.user.created_at)}</div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3"><div className="text-[10px] uppercase text-[#666]">Projects</div><div className="mt-1"><ProjectsBadge n={d.projects} /></div><div className="mt-1 text-[10px] text-[#00F0FF]">Limit: Unlimited</div></div>
              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3"><div className="text-[10px] uppercase text-[#666]">Storage</div><div className="mt-1 text-sm font-bold text-white" data-testid="usage-detail-storage">{d.storage.available ? fmtBytes(d.storage_bytes) : "Unavailable"}</div></div>
              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3"><div className="text-[10px] uppercase text-[#666]">Media files</div><div className="mt-1 text-sm font-bold text-white">{d.media_files}</div></div>
            </div>
            <div>
              <div className="mb-2 text-xs font-bold uppercase tracking-wide text-[#A1A1AA]">Storage breakdown</div>
              {d.storage.available ? (
                <div className="grid grid-cols-2 gap-1.5">
                  {BREAK.map(([k, label]) => (
                    <div key={k} className="flex items-center justify-between rounded border border-white/5 px-2.5 py-1.5 text-xs">
                      <span className="text-[#A1A1AA]">{label}</span>
                      <span className="tabular-nums text-white">{fmtBytes(d.breakdown[k] || 0)}</span>
                    </div>
                  ))}
                </div>
              ) : <div className="rounded border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-xs text-amber-300">Storage breakdown temporarily unavailable.</div>}
            </div>
            <div>
              <div className="mb-2 text-xs font-bold uppercase tracking-wide text-[#A1A1AA]">Projects ({d.project_list.length})</div>
              <div className="space-y-1.5">
                {d.project_list.length === 0 ? <div className="text-xs text-[#666]">No saved projects.</div> :
                  d.project_list.map((p) => (
                    <div key={p.project_id} className="rounded-lg border border-white/5 p-2.5" data-testid="usage-detail-project">
                      <div className="flex items-center justify-between">
                        <span className="truncate text-sm font-semibold text-white">{p.name || "Untitled"}</span>
                        <span className="ml-2 shrink-0 text-xs tabular-nums text-[#00F0FF]">{d.storage.available ? fmtBytes(p.storage_bytes) : "—"}</span>
                      </div>
                      <div className="mt-0.5 text-[11px] text-[#666]">Created {fmtDate(p.created_at)} · Edited {fmtDate(p.updated_at)} · {p.files} files</div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function UserUsage() {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("storage");
  const [direction, setDirection] = useState("desc");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [detailUid, setDetailUid] = useState(null);

  const load = useCallback(async (refresh = 0) => {
    refresh ? setRefreshing(true) : setLoading(true);
    try {
      const d = await api.adminUsersUsage({ q, filter, sort, direction, limit: 100, refresh });
      setData(d);
    } catch (e) {
      setData({ users: [], total: 0, storage: { available: false, error: e?.response?.data?.detail || "Failed to load usage" } });
    } finally { setLoading(false); setRefreshing(false); }
  }, [q, filter, sort, direction]);

  useEffect(() => { load(0); }, [filter, sort, direction]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(() => load(0), 350); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const storageInfo = data?.storage || {};

  return (
    <div data-testid="user-usage" className="text-white">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold">Users → Usage</h2>
          <p className="text-xs text-[#A1A1AA]">Per-user projects, storage and export usage. <span className="text-[#00F0FF]">Unlimited</span> saved projects per user · storage tracked separately.</p>
        </div>
        <div className="flex items-center gap-2">
          {storageInfo.last_calculated && <span className="text-[11px] text-[#666]" data-testid="usage-last-calc">Usage calculated {fmtDate(storageInfo.last_calculated)}</span>}
          <button data-testid="usage-refresh" onClick={() => load(1)} disabled={refreshing}
            className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white/5 disabled:opacity-50">
            <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {storageInfo.available === false && (
        <div data-testid="usage-storage-warning" className="mb-3 flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-300">
          <AlertTriangle size={14} /> {storageInfo.error || "Storage usage temporarily unavailable — project & export counts still shown."}
        </div>
      )}

      {data?.summary && (
        <div data-testid="usage-summary" className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {[
            ["Total users", data.summary.total_users],
            ["Total projects", data.summary.total_projects],
            ["Avg / user", data.summary.avg_projects_per_user],
            ["Total storage", fmtBytes(data.summary.total_storage_bytes)],
            ["Avg storage", fmtBytes(data.summary.avg_storage_bytes)],
            ["Total exports", data.summary.total_exports],
          ].map(([label, val]) => (
            <div key={label} className="rounded-lg border border-white/10 bg-white/[0.02] p-2.5">
              <div className="text-[10px] uppercase tracking-wide text-[#666]">{label}</div>
              <div className="mt-0.5 text-sm font-bold tabular-nums text-white">{val}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#666]" />
          <input data-testid="usage-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / email / id"
            className="w-56 rounded-lg border border-white/15 bg-transparent py-1.5 pl-8 pr-2 text-xs text-white placeholder:text-[#666] focus:border-[#00F0FF]/50 focus:outline-none" />
        </div>
        <select data-testid="usage-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
          className="rounded-lg border border-white/15 bg-[#0b0b0b] py-1.5 px-2 text-xs text-white focus:outline-none">
          {FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select data-testid="usage-sort" value={sort} onChange={(e) => setSort(e.target.value)}
          className="rounded-lg border border-white/15 bg-[#0b0b0b] py-1.5 px-2 text-xs text-white focus:outline-none">
          {SORTS.map(([v, l]) => <option key={v} value={v}>Sort: {l}</option>)}
        </select>
        <button data-testid="usage-direction" onClick={() => setDirection((d) => (d === "desc" ? "asc" : "desc"))}
          className="rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-white/5">{direction === "desc" ? "↓ High→Low" : "↑ Low→High"}</button>
        {data && <span className="ml-auto text-xs text-[#666]">{data.total} user{data.total !== 1 ? "s" : ""}</span>}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-[#A1A1AA]"><Loader2 size={16} className="animate-spin" /> Loading usage…</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-left text-sm" data-testid="usage-table">
            <thead className="bg-white/[0.03] text-[11px] uppercase tracking-wide text-[#A1A1AA]">
              <tr>
                <th className="px-3 py-2.5">User</th>
                <th className="px-3 py-2.5"><FolderOpen size={12} className="mb-0.5 mr-1 inline" />Projects</th>
                <th className="px-3 py-2.5"><HardDrive size={12} className="mb-0.5 mr-1 inline" />Storage</th>
                <th className="px-3 py-2.5">Exports</th>
                <th className="px-3 py-2.5">Media</th>
                <th className="px-3 py-2.5">Last active</th>
                <th className="px-3 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {(data?.users || []).length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-[#666]">No users match.</td></tr>
              ) : data.users.map((u) => (
                <tr key={u.user_id} data-testid="usage-row" onClick={() => setDetailUid(u.user_id)}
                  className="cursor-pointer border-t border-white/5 transition-colors hover:bg-white/[0.03]">
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-white">{u.name || "—"}</div>
                    <div className="text-[11px] text-[#A1A1AA]">{u.email}</div>
                  </td>
                  <td className="px-3 py-2.5"><ProjectsBadge n={u.projects} /></td>
                  <td className="px-3 py-2.5 tabular-nums" data-testid="usage-storage-cell">{u.storage_available ? fmtBytes(u.storage_bytes) : <span className="text-amber-400/80">Unavailable</span>}</td>
                  <td className="px-3 py-2.5 tabular-nums text-[#A1A1AA]">{u.exports}</td>
                  <td className="px-3 py-2.5 tabular-nums text-[#A1A1AA]">{u.media_files}</td>
                  <td className="px-3 py-2.5 text-xs text-[#A1A1AA]">{fmtDate(u.last_active)}</td>
                  <td className="px-3 py-2.5"><span className="rounded-full border border-white/10 px-2 py-0.5 text-[11px] capitalize text-[#A1A1AA]">{u.plan}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detailUid && <DetailDrawer uid={detailUid} onClose={() => setDetailUid(null)} />}
    </div>
  );
}
